import React, { forwardRef, useMemo } from "react";
import { Box, Group, Input, Select, Text, TextInput } from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  DATA_PHONE_COUNTRY_CODES,
  filterPhoneCountryCode,
  isoToFlagEmoji,
} from "../data/phoneCountryCodes";

const PhoneCountrySelectItem = forwardRef(
  ({ label, country, countryZh, iso, ...others }, ref) => (
    <div ref={ref} {...others}>
      <Group spacing="xs" noWrap sx={{ width: "100%" }}>
        <Text component="span" aria-hidden>
          {iso ? isoToFlagEmoji(iso) : "🌐"}
        </Text>
        <Text size="sm" sx={{ flex: 1 }}>
          {country}
          {countryZh ? ` ${countryZh}` : ""}
        </Text>
        <Text size="sm" color="dimmed">
          {label}
        </Text>
      </Group>
    </div>
  )
);

const PhoneEntryFields = ({
  form,
  label,
  required = false,
  disabled = false,
  placeholder = "61210001",
  compact = false,
  size,
  ...props
}) => {
  const { t } = useTranslation();

  const countryCode = form.values?.countryCode ?? "852";
  const countryCodeData = useMemo(() => {
    if (DATA_PHONE_COUNTRY_CODES.some((item) => item.value === countryCode)) {
      return DATA_PHONE_COUNTRY_CODES;
    }
    return [{ value: countryCode, label: `+${countryCode}` }, ...DATA_PHONE_COUNTRY_CODES];
  }, [countryCode]);

  const setField = (field, value) => {
    form.setFieldValue(field, value);
  };

  const countryCodeWidth = compact ? 96 : 90;
  const areaCodeWidth = compact ? 88 : 90;
  const inputSize = size ?? (compact ? "xs" : undefined);
  const countryCodeError = form.errors?.countryCode;
  const areaCodeError = form.errors?.areaCode;
  const numberError = form.errors?.number;
  const hideFieldError = compact ? { error: { display: "none" } } : undefined;

  const fields = (
    <>
      <Select
        disabled={disabled}
        aria-label={t("Country Code")}
        placeholder="+852"
        searchable
        clearable={false}
        nothingFound={t("No options")}
        data={countryCodeData}
        filter={filterPhoneCountryCode}
        itemComponent={PhoneCountrySelectItem}
        withinPortal
        zIndex={1000}
        size={inputSize}
        w={countryCodeWidth}
        sx={{ flexShrink: 0 }}
        styles={{
          dropdown: { minWidth: 280 },
          ...(compact ? hideFieldError : undefined),
        }}
        value={countryCode}
        onChange={(value) => setField("countryCode", value ?? "852")}
        error={countryCodeError}
      />
      <TextInput
        disabled={disabled}
        aria-label={t("Area Code")}
        placeholder={compact ? t("Area") : t("Area Code")}
        size={inputSize}
        w={areaCodeWidth}
        sx={{ flexShrink: 0 }}
        styles={compact ? hideFieldError : undefined}
        value={form.values?.areaCode ?? ""}
        onChange={(event) => setField("areaCode", event.currentTarget.value)}
        error={areaCodeError}
      />
      <Box sx={{ flex: "1 1 0", minWidth: 0, width: 0 }}>
        <TextInput
          disabled={disabled}
          aria-label={t("Phone Number")}
          placeholder={placeholder}
          required={required}
          size={inputSize}
          sx={{ width: "100%" }}
          value={form.values?.number ?? ""}
          onChange={(event) => setField("number", event.currentTarget.value)}
          error={numberError}
        />
      </Box>
    </>
  );

  return (
    <Input.Wrapper
      label={label ? t(label) : undefined}
      required={required}
      withAsterisk={required}
      sx={{ width: "100%", minWidth: 0 }}
      {...props}
    >
      {compact ? (
        <Box
          sx={{
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            flexWrap: "nowrap",
            width: "100%",
            minWidth: 0,
            marginTop: label ? 4 : 0,
          }}
        >
          {fields}
        </Box>
      ) : (
        <Group
          spacing="xs"
          align="flex-end"
          mt={label ? 4 : 0}
          noWrap
          sx={{ width: "100%", minWidth: 0 }}
        >
          {fields}
        </Group>
      )}
    </Input.Wrapper>
  );
};

export default PhoneEntryFields;
